import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  getSelfServiceBaseUrl,
  getSelfServiceEntityColumnsUpdateApiUrl,
  getSelfServicePlatformApiUrl,
} from '../shared/self-service-api.utils';

export interface RuleExecutionRequest {
  input: Record<string, unknown>;
  rules: Array<{
    ruleId: string;
    outputField: string;
    expression: string;
    description?: string | null;
  }>;
}

function buildPlatformApiUrl(path: string): string {
  const base = getSelfServiceBaseUrl();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalizedPath}`;
}

@Injectable({
  providedIn: 'root',
})
export class SpricedLiteApiService {
  constructor(private readonly http: HttpClient) {}

  get apiBaseUrl(): string {
    return getSelfServiceBaseUrl();
  }

  analyzeWorkbook(file: File, sheetName?: string | null): Observable<Record<string, unknown>> {
    const formData = new FormData();
    formData.append('file', file);
    const url = sheetName?.trim()
      ? buildPlatformApiUrl(`/excel/analyze?sheetName=${encodeURIComponent(sheetName.trim())}`)
      : buildPlatformApiUrl('/excel/analyze');
    return this.http.post<Record<string, unknown>>(url, formData);
  }

  analyzeDeamWorkbook(file: File, sheetName?: string | null): Observable<Record<string, unknown>> {
    const formData = new FormData();
    formData.append('file', file);
    const url = sheetName?.trim()
      ? buildPlatformApiUrl(`/deam/analyze?sheetName=${encodeURIComponent(sheetName.trim())}`)
      : buildPlatformApiUrl('/deam/analyze');
    return this.http.post<Record<string, unknown>>(url, formData);
  }

  previewWorkflow(request: {
    files: File[];
    workflowName: string;
    entityName: string;
    primaryKey: string;
    userId: string;
    sheetName?: string | null;
    filePlanJson?: string | null;
  }): Observable<Record<string, unknown>> {
    const formData = new FormData();
    request.files.forEach((file) => formData.append('files', file));
    formData.append('workflowName', request.workflowName);
    formData.append('entityName', request.entityName);
    formData.append('primaryKey', request.primaryKey);
    formData.append('user_id', request.userId);
    if (request.sheetName?.trim()) {
      formData.append('sheetName', request.sheetName.trim());
    }
    if (request.filePlanJson?.trim()) {
      formData.append('filePlanJson', request.filePlanJson.trim());
    }
    return this.http.post<Record<string, unknown>>(buildPlatformApiUrl('/rules/preview-workflow'), formData);
  }

  persistReviewedWorkflow(request: {
    files: File[];
    workflowName: string;
    entityName: string;
    primaryKey: string;
    userId: string;
    workflowConfig: string;
    deamDefinitionJson: string;
    sheetName?: string | null;
    filePlanJson?: string | null;
  }): Observable<Record<string, unknown>> {
    const formData = new FormData();
    request.files.forEach((file) => formData.append('files', file));
    formData.append('workflowName', request.workflowName);
    formData.append('entityName', request.entityName);
    formData.append('primaryKey', request.primaryKey);
    formData.append('user_id', request.userId);
    formData.append('workflowConfig', request.workflowConfig);
    formData.append('deamDefinitionJson', request.deamDefinitionJson);
    if (request.sheetName?.trim()) {
      formData.append('sheetName', request.sheetName.trim());
    }
    if (request.filePlanJson?.trim()) {
      formData.append('filePlanJson', request.filePlanJson.trim());
    }
    return this.http.post<Record<string, unknown>>(buildPlatformApiUrl('/rules/persist-workflow'), formData);
  }

  persistWorkflowAsync(request: {
    files: File[];
    workflowName: string;
    entityName: string;
    primaryKey: string;
    userId: string;
    workflowConfig: string;
    deamDefinitionJson: string;
    sheetName?: string | null;
    filePlanJson?: string | null;
  }): Observable<Record<string, unknown>> {
    const formData = new FormData();
    request.files.forEach((file) => formData.append('files', file));
    formData.append('workflowName', request.workflowName);
    formData.append('entityName', request.entityName);
    formData.append('primaryKey', request.primaryKey);
    formData.append('user_id', request.userId);
    formData.append('workflowConfig', request.workflowConfig);
    formData.append('deamDefinitionJson', request.deamDefinitionJson);
    if (request.sheetName?.trim()) {
      formData.append('sheetName', request.sheetName.trim());
    }
    if (request.filePlanJson?.trim()) {
      formData.append('filePlanJson', request.filePlanJson.trim());
    }
    return this.http.post<Record<string, unknown>>(buildPlatformApiUrl('/rules/persist-workflow-async'), formData);
  }

  getIngestionStatus(jobId: string): Observable<Record<string, unknown>> {
    return this.http.get<Record<string, unknown>>(buildPlatformApiUrl(`/rules/persist-status/${jobId}`));
  }

  persistDeamDefinition(file: File, payload: string, sheetName?: string | null): Observable<Record<string, unknown>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('payload', payload);
    if (sheetName?.trim()) {
      formData.append('sheetName', sheetName.trim());
    }
    return this.http.post<Record<string, unknown>>(buildPlatformApiUrl('/deam/persist'), formData);
  }

  updateEntityColumns(entityName: string, payload: unknown): Observable<Record<string, unknown>> {
    return this.http.post<Record<string, unknown>>(getSelfServiceEntityColumnsUpdateApiUrl(entityName), payload);
  }

  updateWorkflowStore(payload: unknown): Observable<Record<string, unknown>> {
    return this.http.post<Record<string, unknown>>(getSelfServicePlatformApiUrl(), payload);
  }

  executeRules(request: RuleExecutionRequest): Observable<Record<string, unknown>> {
    return this.http.post<Record<string, unknown>>(buildPlatformApiUrl('/rules/execute'), request);
  }

  createRuleFromFormula(
    formula: string,
    outputField: string,
    cellMapping: Record<string, string>,
  ): Observable<Record<string, unknown>> {
    return this.http.post<Record<string, unknown>>(buildPlatformApiUrl('/rules/from-formula'), {
      formula,
      outputField,
      cellMapping,
    });
  }

  buildCellMapping(columns: Array<{ columnLetter: string; fieldName: string }>): Record<string, string> {
    return columns.reduce<Record<string, string>>((acc, col) => {
      if (col.columnLetter && col.fieldName) {
        acc[col.columnLetter] = col.fieldName;
      }
      return acc;
    }, {});
  }

  buildSheetCellMapping(columns: Array<{ columnLetter: string; fieldName: string }>): string {
    return JSON.stringify(this.buildCellMapping(columns), null, 2);
  }

  parseCellMapping(rawValue: string): Record<string, string> {
    const value = rawValue.trim();
    if (!value) return {};
    const parsed = JSON.parse(value) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error('Cell mapping must be a JSON object.');
    }
    return Object.entries(parsed as Record<string, unknown>).reduce<Record<string, string>>(
      (acc, [key, val]) => {
        if (typeof val === 'string' && key.trim()) {
          acc[key.trim()] = val.trim();
        }
        return acc;
      },
      {},
    );
  }
}
